import { AdmMember } from "@custom-types/base-types";
import { createSlice } from "@reduxjs/toolkit";
import { FetchingStatus } from "@store/store-helper";
import {
  removeMemberFromCompany,
  fetchMember,
  updateMember,
  fetchMemberProjects,
  changeMemberAuthMethod,
} from "@store/member/member-thunks";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** State for member */
type MemberState = {
  /** The selected member */
  member: AdmMember | null;

  /** The projects of the selected member */
  membersProjects: FaroCoreApiTypes.AdmMembersProjects[];

  /** Async operation statuses for member-slice */
  fetchingMember: {
    /** The status of fetching the selected member */
    selectedMember: FetchingStatus;

    /** The status of updating the selected member */
    updatingMember: FetchingStatus;

    /** The status of changing the authentication method of the selected member */
    changingAuthMethod: FetchingStatus;

    /** The status of deleting the member from a company */
    deletingMemberFromCompany: FetchingStatus;

    /** The status of fetching the projects of the member */
    membersProjects: FetchingStatus;
  };
};

/** Initial state for member slice */
const initialMemberState: MemberState = {
  /** By default, no member is selected */
  member: null,

  membersProjects: [],

  /** By default, the fetch status is uninitialized */
  fetchingMember: {
    selectedMember: "uninitialized",
    deletingMemberFromCompany: "uninitialized",
    updatingMember: "uninitialized",
    changingAuthMethod: "uninitialized",
    membersProjects: "uninitialized",
  },
};

/** Slice to manage member state */
const memberSlice = createSlice({
  name: "member",
  initialState: initialMemberState,
  reducers: {
    /** Resets the member state to its initial values */
    resetMemberState: () => initialMemberState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMember.pending, (state) => {
        state.fetchingMember.selectedMember = "pending";
      })
      .addCase(fetchMember.fulfilled, (state, action) => {
        state.member = action.payload;
        state.fetchingMember.selectedMember = "succeeded";
      })
      .addCase(fetchMember.rejected, (state) => {
        state.fetchingMember.selectedMember = "rejected";
      })

      .addCase(removeMemberFromCompany.pending, (state) => {
        state.fetchingMember.deletingMemberFromCompany = "pending";
      })
      .addCase(removeMemberFromCompany.fulfilled, (state, action) => {
        if (state.member) {
          const updatedCompanies = state.member.companies.filter(
            (company) => company.id !== action.payload.companyId
          );
          state.member = {
            ...state.member,
            companies: updatedCompanies,
          };
        }

        state.fetchingMember.deletingMemberFromCompany = "succeeded";
      })
      .addCase(removeMemberFromCompany.rejected, (state) => {
        state.fetchingMember.deletingMemberFromCompany = "rejected";
      })

      .addCase(updateMember.pending, (state) => {
        state.fetchingMember.updatingMember = "pending";
      })
      .addCase(updateMember.fulfilled, (state, action) => {
        if (!state.member) {
          return;
        }
        state.member = {
          ...state.member,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          email: action.payload.email,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- external api
          emailVerified: action.payload.isEmailVerified,
        };

        state.fetchingMember.updatingMember = "succeeded";
      })
      .addCase(updateMember.rejected, (state) => {
        state.fetchingMember.updatingMember = "rejected";
      })

      .addCase(changeMemberAuthMethod.pending, (state) => {
        state.fetchingMember.changingAuthMethod = "pending";
      })
      .addCase(changeMemberAuthMethod.fulfilled, (state, action) => {
        state.member = action.payload;
        state.fetchingMember.changingAuthMethod = "succeeded";
      })
      .addCase(changeMemberAuthMethod.rejected, (state) => {
        state.fetchingMember.changingAuthMethod = "rejected";
      })

      .addCase(fetchMemberProjects.pending, (state) => {
        state.fetchingMember.membersProjects = "pending";
      })
      .addCase(fetchMemberProjects.fulfilled, (state, action) => {
        state.membersProjects = action.payload;

        state.fetchingMember.membersProjects = "succeeded";
      })
      .addCase(fetchMemberProjects.rejected, (state) => {
        state.fetchingMember.membersProjects = "rejected";
      });
  },

  selectors: {
    /** Selector to get the entire member state */
    memberStateSelector: (state) => state,
  },
});

export const { resetMemberState } = memberSlice.actions;

export const { memberStateSelector } = memberSlice.selectors;

export const memberReducer = memberSlice.reducer;
